import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { formatTimeAgo, getFormattedDateValue } from "helpers/Datetime.helper";
import { getCurrentLangName } from "helpers/String.helper";
import * as BlockApi from "api/student/v2/block.api";
import * as UserInterface from "interfaces/User.interface";

const BlockPage = () => {
  const navigate = useNavigate();
  const [isInit, setIsInit] = useState(false);
  const [blockInfo, setBlockInfo] = useState<UserInterface.IBlockObj | null>(null);
  const [resolveRequestList, setResolveRequestList] = useState<UserInterface.IBlockResolveRequestObj[]>([]);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsInit(false);
      setErrorMessage("");

      const [blockInfoResponse, resolveRequestListResponse] = await Promise.all([
        BlockApi.getBlockInfo(),
        BlockApi.getResolveRequestList()
      ]);

      if (blockInfoResponse?.blockInfo) {
        setBlockInfo(blockInfoResponse.blockInfo);
      } else {
        // 차단 정보가 없으면 지망생 홈으로 이동
        alert("차단이 해제되었습니다. 다시 로그인해주세요.");
        navigate('/logout', { replace: true });
        return;
      }

      if (resolveRequestListResponse?.resolveRequestList) {
        setResolveRequestList(resolveRequestListResponse.resolveRequestList);
      }

      setIsInit(true);
    } catch (error: any) {
      console.error("Failed to fetch block data:", error);
      setErrorMessage(error?.message || getLanguageValue(PAGE_TYPE.STUDENT, "block.txtErrorLoadingBlockInfo"));
      setIsInit(true);
    }
  };

  const handleSubmitRequest = async () => {
    if (!reason.trim()) {
      alert(getLanguageValue(PAGE_TYPE.STUDENT, "block.txtReasonPlaceholder"));
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await BlockApi.createResolveRequest({ reason: reason.trim() });

      if (response) {
        alert(getLanguageValue(PAGE_TYPE.STUDENT, "block.txtRequestSuccess"));
        setReason("");
        // 목록 새로고침
        const resolveRequestListResponse = await BlockApi.getResolveRequestList();
        if (resolveRequestListResponse?.resolveRequestList) {
          setResolveRequestList(resolveRequestListResponse.resolveRequestList);
        }
      }
    } catch (error: any) {
      console.error("Failed to submit resolve request:", error);
      alert(error?.message || getLanguageValue(PAGE_TYPE.STUDENT, "block.txtRequestFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeColor = (statusCode: string) => {
    if (statusCode.includes("waiting")) return "bg-yellow-100 text-yellow-800";
    if (statusCode.includes("approved")) return "bg-green-100 text-green-800";
    if (statusCode.includes("rejected")) return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <main className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
        {/* 헤더 */}
        <div className="fixed top-0 left-0 w-full bg-gray-50/80 backdrop-blur-md z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
            <div className="w-10"></div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtTitle")}
            </h1>
            <button
              onClick={fetchData}
              disabled={!isInit}
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh"
            >
              <svg
                className={`w-6 h-6 text-gray-700 ${!isInit ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
        {!isInit ? (
          // 로딩 스켈레톤
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
              <div className="bg-gray-200 rounded h-6 w-32 mb-4"></div>
              <div className="space-y-3">
                <div className="bg-gray-200 rounded h-4 w-full"></div>
                <div className="bg-gray-200 rounded h-4 w-3/4"></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
              <div className="bg-gray-200 rounded h-6 w-48 mb-4"></div>
              <div className="space-y-2">
                <div className="bg-gray-200 rounded h-20 w-full"></div>
              </div>
            </div>
          </div>
        ) : errorMessage ? (
          // 에러 메시지
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <p className="text-red-600 font-medium">{errorMessage}</p>
            <button
              onClick={fetchData}
              className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtRetry")}
            </button>
          </div>
        ) : (
          <div className="space-y-6 pb-24">
            {/* 차단 정보 섹션 */}
            {blockInfo && (
              <section className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="space-y-4">
                  {/* 차단 사유 */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2">
                      {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtBlockReason")}
                    </h3>
                    <p className="text-base text-gray-900 bg-red-50 p-4 rounded-lg border border-red-100">
                      {blockInfo.reason}
                    </p>
                  </div>

                  {/* 차단 일시 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtBlockedAt")}
                      </h3>
                      <p className="text-sm text-gray-700">
                        {getFormattedDateValue(blockInfo.createdAt, "YYYY.MM.DD HH:mm")}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        ({formatTimeAgo(blockInfo.createdAt)})
                      </p>
                    </div>

                    {/* 차단 해제 예정 */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtBlockEndAt")}
                      </h3>
                      <p className="text-sm text-gray-700">
                        {getFormattedDateValue(blockInfo.blockingEndAt, "YYYY.MM.DD HH:mm")}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* 차단 해제 요청 폼 */}
            <section className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtRequestFormTitle")}
              </h2>

              {resolveRequestList.some(request => request.blockResolveRequestStatusCode.includes("waiting")) ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-semibold text-yellow-800 mb-1">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtAlreadyHasPendingRequest")}
                      </h3>
                      <p className="text-sm text-yellow-700">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtAlreadyHasPendingRequestDesc")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtReasonLabel")}
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={getLanguageValue(PAGE_TYPE.STUDENT, "block.txtReasonPlaceholder")}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent resize-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  <button
                    onClick={handleSubmitRequest}
                    disabled={isSubmitting || !reason.trim()}
                    className="w-full py-3 px-4 bg-[#CF3688] text-white font-medium rounded-lg hover:bg-[#B02E73] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting
                      ? getLanguageValue(PAGE_TYPE.STUDENT, "block.btnSubmitting")
                      : getLanguageValue(PAGE_TYPE.STUDENT, "block.btnSubmitRequest")}
                  </button>
                </div>
              )}
            </section>

            {/* 차단 해제 요청 목록 */}
            <section className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtResolveRequestListTitle")}
              </h2>

              {resolveRequestList.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtNoResolveRequest")}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {resolveRequestList.map((request) => (
                    <div
                      key={request.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                                request.blockResolveRequestStatusCode
                              )}`}
                            >
                              {getCurrentLangName(request.blockResolveRequestStatus.names)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatTimeAgo(request.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{request.reason}</p>
                          <p className="text-xs text-gray-500">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtRequestedAt")}:{" "}
                            {getFormattedDateValue(request.createdAt, "YYYY.MM.DD HH:mm")}
                          </p>
                        </div>
                      </div>

                      {/* 원래 차단 정보 */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">
                          {getLanguageValue(PAGE_TYPE.STUDENT, "block.txtBlockReason")}
                        </p>
                        <p className="text-xs text-gray-700">{request.adminUserBlock.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 로그아웃 버튼 */}
            <section className="mt-6">
              <Link
                to="/logout"
                className="block w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-center"
              >
                {getLanguageValue(PAGE_TYPE.STUDENT, "my.btnLogout")}
              </Link>
            </section>
          </div>
        )}
      </div>
    </main>
  );
};

export default BlockPage;

